import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, ThumbsUp, CheckCircle, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ProductReviews({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState({ can_review: false });
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, title: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadReviews();
    checkCanReview();
  }, [productId, page]);

  // Honor deep-link params from the post-purchase review-request email.
  // `?rate=N&open_review=1` pre-fills the star rating and auto-opens
  // the inline review form. Runs once per mount — clears the params
  // afterward so refresh/back-button doesn't keep re-prefilling.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    const rate = parseInt(sp.get('rate') || '', 10);
    const open = sp.get('open_review') === '1';
    if (rate >= 1 && rate <= 5) {
      setNewReview((prev) => ({ ...prev, rating: rate }));
    }
    if (open) {
      setShowForm(true);
      // Strip the params so a refresh doesn't keep re-triggering.
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);


  const loadReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reviews/product/${productId}?page=${page}&limit=5`);
      setReviews(res.data.reviews || []);
      setReviewStats({
        average: res.data.average_rating,
        total: res.data.total,
        pages: res.data.pages,
        breakdown: res.data.rating_breakdown
      });
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanReview = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await axios.get(`${API_URL}/api/reviews/can-review/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanReview(res.data);
    } catch (error) {
      console.error('Failed to check review status:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    // Quick-review mode: rating alone is enough to submit. Title +
    // comment stay optional so power reviewers can still write a real
    // writeup, but most buyers land here from the post-purchase email
    // with their rating already chosen and just need one more click.
    if (!newReview.rating || newReview.rating < 1 || newReview.rating > 5) {
      toast.error('Please pick a star rating');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/reviews`,
        {
          product_id: productId,
          rating: newReview.rating,
          title: (newReview.title || '').trim(),
          comment: (newReview.comment || '').trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thanks for your review!');
      setShowForm(false);
      setNewReview({ rating: 5, title: '', comment: '' });
      loadReviews();
      setCanReview({ can_review: false, reason: 'already_reviewed' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (reviewId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to mark reviews as helpful');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/reviews/${reviewId}/helpful`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadReviews();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'fill-[#00F0FF] text-[#00F0FF]'
                : 'text-neutral-600'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={() => interactive && onChange && onChange(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      {reviewStats && reviewStats.total > 0 && (
        <div className="flex flex-col md:flex-row gap-8 p-6 bg-neutral-900/50 rounded-2xl border border-white/10">
          <div className="text-center">
            <div className="text-5xl font-bold mb-2">{reviewStats.average.toFixed(1)}</div>
            {renderStars(Math.round(reviewStats.average))}
            <p className="text-sm text-neutral-400 mt-2">{reviewStats.total} reviews</p>
          </div>
          <div className="flex-1 space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = reviewStats.breakdown[stars] || 0;
              const percentage = reviewStats.total > 0 ? (count / reviewStats.total) * 100 : 0;
              return (
                <div key={stars} className="flex items-center gap-3">
                  <span className="w-12 text-sm text-neutral-400">{stars} stars</span>
                  <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-12 text-sm text-neutral-400 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Write Review Button */}
      {canReview.can_review && !showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
        >
          <Star className="w-4 h-4 mr-2" />
          Write a Review
          {canReview.is_verified_purchase && (
            <span className="ml-2 text-xs bg-black/20 px-2 py-0.5 rounded">Verified Purchase</span>
          )}
        </Button>
      )}

      {/* Review Form */}
      {showForm && (
        <form onSubmit={handleSubmitReview} className="p-6 bg-neutral-900/50 rounded-2xl border border-white/10">
          <h3 className="text-lg font-bold mb-1">How would you rate this?</h3>
          <p className="text-sm text-neutral-400 mb-5">
            Just pick a star rating — that&apos;s it. Want to share more? The fields below are optional.
          </p>

          <div className="mb-5">
            <label className="block text-sm text-neutral-400 mb-2">Your rating</label>
            {renderStars(newReview.rating, true, (rating) => setNewReview({ ...newReview, rating }))}
          </div>

          <div className="mb-4">
            <label className="block text-sm text-neutral-400 mb-2">
              Title <span className="text-neutral-600 font-normal">(optional)</span>
            </label>
            <Input
              data-testid="review-title-input"
              value={newReview.title}
              onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
              placeholder="Sum it up in a line"
              className="bg-neutral-800 border-white/10"
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm text-neutral-400 mb-2">
              Your thoughts <span className="text-neutral-600 font-normal">(optional)</span>
            </label>
            <Textarea
              data-testid="review-comment-input"
              value={newReview.comment}
              onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
              placeholder="What worked, what didn't, who should buy this?"
              className="bg-neutral-800 border-white/10"
              rows={4}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="submit-review-btn"
              type="submit"
              disabled={submitting}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold"
            >
              {submitting
                ? 'Submitting...'
                : (newReview.title.trim() || newReview.comment.trim())
                  ? 'Submit review'
                  : 'Submit rating'}
            </Button>
            <Button
              data-testid="cancel-review-btn"
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              className="border-white/20"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <div className="text-center py-12 text-neutral-400">
            <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No reviews yet. Be the first to review!</p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.review_id} className="p-6 bg-neutral-900/50 rounded-2xl border border-white/10">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {review.user_picture ? (
                    <img src={review.user_picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center">
                      <User className="w-5 h-5 text-neutral-500" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{review.user_name}</span>
                      {review.is_verified_purchase && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-neutral-400">{formatDate(review.created_at)}</span>
                  </div>
                </div>
                {renderStars(review.rating)}
              </div>
              
              <h4 className="font-semibold mb-2">{review.title}</h4>
              <p className="text-neutral-300 mb-4">{review.comment}</p>
              
              <button
                onClick={() => handleHelpful(review.review_id)}
                className="flex items-center gap-2 text-sm text-neutral-400 hover:text-[#00F0FF] transition-colors"
              >
                <ThumbsUp className="w-4 h-4" />
                Helpful ({review.helpful_count || 0})
              </button>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {reviewStats && reviewStats.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="border-white/20"
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-neutral-400">
            Page {page} of {reviewStats.pages}
          </span>
          <Button
            variant="outline"
            disabled={page === reviewStats.pages}
            onClick={() => setPage(page + 1)}
            className="border-white/20"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default ProductReviews;
